import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { SwitchTransition, CSSTransition } from 'react-transition-group';
import * as Yup from 'yup';

// COMPONENTS
import DateInput from '../../components/DateInput';
import SingleSelect from '../../components/SingleSelect';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// TYPES
import { TrainingEditCommand } from '../../api/models';
import { closeModalType } from '../../components/GlobalModal';

interface EditDeadlineModalTypes {
	item?: TrainingEditCommand;
	closeModal?: closeModalType;
	getTrainingData?: () => {};
}

interface formTypes {
	deadlineType?: string;
	fixedDate?: string;
	timeFrom?: string;
}

const EditDeadlineModal: React.FC<EditDeadlineModalTypes> = ({ item, closeModal, getTrainingData }) => {
	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Get all Training Timeframes from Redux store
	const trainingTimeFrames = useAppSelector((state) => state.lists.trainingTimeFrames);

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	// Initial Values
	const initialFormValues: formTypes = {
		deadlineType: item!.deadlineTimeFrameType ? 'timeFrom' : item!.deadlineDate ? 'fixed' : 'none',
		fixedDate: item!.deadlineDate || '',
		timeFrom: item!.deadlineTimeFrameType || ''
	};

	const formValidation = Yup.object({
		fixedDate: Yup.string().when('deadlineType', { is: 'fixed', then: Yup.string().required('Required') }),
		timeFrom: Yup.string().when('deadlineType', { is: 'timeFrom', then: Yup.string().required('Required') })
	});

	// Submit form to api
	const submitFormData = async (values: any) => {
		setIsLoading(true);

		const data: TrainingEditCommand = {
			id: item!.id,
			profileType: item!.profileType,
			deadlineDate: values.deadlineType === 'fixed' ? values.fixedDate : '',
			deadlineTimeFrameType: values.deadlineType === 'timeFrom' ? values.timeFrom : ''
		};

		const response = await apibridge.postTrainingEdit(data);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			if (closeModal) {
				closeModal();
			}

			if (getTrainingData) {
				getTrainingData();
			}
		}
	};

	return (
		<div className="edit-deadline-modal">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<Formik
								validationSchema={formValidation}
								initialValues={initialFormValues}
								onSubmit={submitFormData}
								enableReinitialize
							>
								{(formik) => {
									return (
										<Form translate={'yes'}>
											<div className="form-group">
												<p className="h5">Set completion deadline</p>
											</div>

											<div className="form-group">
												<div className="radio-form-container">
													<div className="form-control-radio">
														<Field name="deadlineType" type="radio" value="none" disabled={!isOrganisationActive} />

														<label htmlFor="deadlineType">No Deadline</label>
													</div>
												</div>
												<div className="radio-form-container">
													<div className="form-control-radio">
														<Field name="deadlineType" type="radio" value="fixed" disabled={!isOrganisationActive} />

														<label htmlFor="deadlineType">Fixed Date</label>
													</div>

													<Field
														children={() => (
															<div className={`date-form-container`}>
																<DateInput
																	className={`${formik.values.deadlineType === 'fixed' ? 'pointer' : ''}`}
																	disabled={formik.values.deadlineType !== 'fixed' || !isOrganisationActive}
																	dateTimeString={formik.values.fixedDate || ''}
																	onChange={(val: string) => formik.setFieldValue('fixedDate', val)}
																/>

																<ErrorMessage name="fixedDate" component="div" className="form-validation-error" />
															</div>
														)}
													/>
												</div>{' '}
												<div className="radio-form-container">
													<div className="form-control-radio">
														<Field name="deadlineType" type="radio" value="timeFrom" disabled={!isOrganisationActive} />

														<label htmlFor="deadlineType">Time from assigned</label>
													</div>

													<Field
														children={() => (
															<div className="date-form-container">
																<SingleSelect
																	className={`${formik.values.deadlineType === 'timeFrom' ? 'pointer' : ''}`}
																	isDisabled={formik.values.deadlineType !== 'timeFrom' || !isOrganisationActive}
																	options={trainingTimeFrames}
																	placeholder="Select length of time"
																	value={formik.values.timeFrom}
																	onChange={(val: string) => formik.setFieldValue('timeFrom', val)}
																/>

																<ErrorMessage name="timeFrom" component="div" className="form-validation-error" />
															</div>
														)}
													/>
												</div>
											</div>

											<div className="form-group">
												<Button
													disabled={isLoading || !isOrganisationActive}
													type="submit"
													className="btn-primary full-width"
												>
													Update
												</Button>
											</div>
										</Form>
									);
								}}
							</Formik>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default EditDeadlineModal;
